import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { TmBaseComponent } from '@treasury/presentation';
import {
    circleCheckmarkIcon,
    circleCloseIcon,
    circleWarningIcon,
    informationIcon,
} from '@treasury/presentation/assets/icons';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { HeaderBarService } from '../services/jhd-header-bar.service';

export enum ConfirmationType {
    Success = 'success',
    Error = 'error',
    Warning = 'warning',
    Pending = 'pending',
}

export const tagName = 'confirmation-container';
@customElement(tagName)
export class ConfirmationContainer extends TmBaseComponent {
    @property()
    public confirmationType: ConfirmationType = ConfirmationType.Success;

    @property()
    public headingText = '';

    @property()
    public subheadingText = '';

    @property()
    public iconSize: 'large' | 'small' = 'large';

    @InjectProperty()
    declare private headerService: HeaderBarService;

    private get iconConfig() {
        switch (this.confirmationType) {
            case ConfirmationType.Success:
                return { icon: circleCheckmarkIcon, color: `var(--content-positive)` };
            case ConfirmationType.Error:
                return { icon: circleCloseIcon, color: `var(--content-negative)` };
            case ConfirmationType.Warning:
                return { icon: circleWarningIcon, color: `var(--content-attention)` };
            case ConfirmationType.Pending:
                return { icon: informationIcon, color: `var(--content-pending)` };
            default:
                return { icon: circleCheckmarkIcon, color: `var(--content-positive)` };
        }
    }

    protected firstUpdated(): void {
        this.headerService.configure({
            title: '',
            subtitle: '',
            empty: true,
        });
    }

    protected render() {
        const iconSize = this.iconSize === 'small' ? '50px' : '106px';
        return html`
            <slot name="header"></slot>
            <div class="flex items-center flex-col pb-2 border-b border-dashed">
                <tm-icon
                    class="confirmation-icon pt-12"
                    .color=${this.iconConfig.color}
                    .icon=${this.iconConfig.icon}
                    .width=${iconSize}
                    .height=${iconSize}
                ></tm-icon>
                <h2 class="pt-2 text-lg text-header">${this.headingText}</h2>
                <p class="text-md text-secondary">${this.subheadingText}</p>
            </div>
            <slot></slot>
        `;
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                    background-color: var(--container-primary);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ConfirmationContainer;
    }
}
